/**
 * The Employees directory's four tasks (#270, Employees V1.3): Arbeidstid, Ansattdata, Ikke invitert,
 * Venter på innlogging. One rule per task, read by the indicator counts, the list, the correction
 * queue and the profile, so a count and the people it opens can never disagree.
 *
 * Every predicate reads the authoritative facts the API sends and says «unknown» where the reader is
 * not sent them, never «none»: a manager who is not the administrator receives no invitation facts,
 * and a reader without the personnel record receives no agreed week or start date.
 */
import type { ApiUser } from '@/api/data';
import { fullName } from '@/ui/format';
import { type Who, matches, whoOf } from './roster';

export type TaskKey = 'hours' | 'setup' | 'uninvited' | 'pending';
/**
 * 'number' is the narrow list Innstillinger's follow-up opens (#267 SET-04): active people missing only
 * the employee number the pay file needs. It is no indicator of its own; it narrows Ansattdata.
 */
export type Task = TaskKey | 'all' | 'number';
export const TASKS: TaskKey[] = ['hours', 'setup', 'uninvited', 'pending'];
/** the address names the task in Norwegian, as the rest of the app's parameters */
const PARAM: Record<TaskKey, string> = {
  hours: 'arbeidstid',
  setup: 'ansattdata',
  uninvited: 'ikke-invitert',
  pending: 'venter',
};

/**
 * The agreed week as the rules see it (#54, plan 0027 B): the person's own, else the company's, which
 * the API has already resolved into max_hours_per_week. A full-timer who follows the company's week
 * has no own figure and that is complete; only no week at all, or a stored 0 or less, is an issue.
 */
export type Hours = 'own' | 'company' | 'missing' | 'invalid' | 'unknown';
export function hoursOf(u: Pick<ApiUser, 'max_hours_per_week' | 'agreed_week_hours'>): Hours {
  const w = u.max_hours_per_week;
  if (w === undefined) return 'unknown';
  if (w == null) return 'missing';
  if (w <= 0) return 'invalid';
  return u.agreed_week_hours == null ? 'company' : 'own';
}

/**
 * The employment record a payroll file and the rules need: number, start date and birth date, and a
 * stored number the company's payroll system would refuse ('number_format', the server's own rule).
 */
export type SetupGap = 'employee_id' | 'number_format' | 'employment_start' | 'birth_date';
type SetupFacts = Pick<ApiUser, 'employee_id' | 'birth_date_missing' | 'number_problem'> & {
  employment_start?: string | null;
};
export function setupGaps(u: SetupFacts): SetupGap[] | null {
  // a reader who is not sent the number is not sent the record: unknown, not complete
  if (u.employee_id === undefined) return null;
  const gaps: SetupGap[] = [];
  if (!u.employee_id) gaps.push('employee_id');
  if (u.number_problem) gaps.push('number_format');
  if (u.employment_start !== undefined && !u.employment_start) gaps.push('employment_start');
  if (u.birth_date_missing) gaps.push('birth_date');
  return gaps;
}

/**
 * Got in to this company, invited and waiting, never invited; null when the reader is not sent these facts.
 * «In» is a session in THIS company (accessed_at, PROM-28), the rule Kom i gang's access step reads; a
 * person who logged in to another company has an account and waits for their first session here.
 */
export type Access = 'in' | 'pending' | 'uninvited';
export const accessedHere = (u: Pick<ApiUser, 'last_login_at' | 'accessed_at'>) =>
  u.accessed_at !== undefined ? u.accessed_at : (u.last_login_at ?? null);
export function accessOf(
  u: Pick<ApiUser, 'last_login_at' | 'invited_at' | 'accessed_at'>,
  known: boolean,
): Access | null {
  if (!known) return null;
  if (accessedHere(u)) return 'in';
  return u.invited_at || u.last_login_at ? 'pending' : 'uninvited';
}

export type Row = ApiUser & { employment_start?: string | null };
/** Whether a person is in a task; null when the facts it needs are unknown to this reader. */
export function inTask(u: Row, task: Task, accessKnown: boolean): boolean | null {
  if (task === 'all') return true;
  if (task === 'hours') {
    const h = hoursOf(u);
    return h === 'unknown' ? null : h === 'missing' || h === 'invalid';
  }
  if (task === 'setup') {
    const g = setupGaps(u);
    return g == null ? null : g.length > 0;
  }
  if (task === 'number') {
    const g = setupGaps(u);
    return g == null ? null : u.active_status !== false && g.includes('employee_id');
  }
  const a = accessOf(u, accessKnown);
  // only somebody active is someone to invite; an inactive person cannot log in (plan 0016 P0.16)
  return a == null ? null : u.active_status && a === task;
}

/** The directory's scope: everything but the task. The counts and the list both read it. */
export interface Scope {
  q: string;
  /** a team id, 'none' for people in no team, or null for every team */
  team: number | 'none' | null;
  who: Who;
}
export const teamsHere = (u: Row, tenantId: number | undefined) =>
  (u.teams ?? []).filter((t) => {
    const tid = (t as { tenant_id?: number }).tenant_id;
    return tid == null || tid === tenantId;
  });

/** People in scope, by name then by id, so two people with one name keep a fixed order. */
export function inScope(users: Row[], s: Scope, tenantId: number | undefined): Row[] {
  return users
    .filter((u) => (s.who === 'alle' ? true : s.who === 'aktive' ? u.active_status : !u.active_status))
    .filter((u) => {
      if (s.team == null) return true;
      const ts = teamsHere(u, tenantId);
      return s.team === 'none' ? ts.length === 0 : ts.some((t) => t.id === s.team);
    })
    .filter((u) => matches(u, s.q))
    .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb') || a.id - b.id);
}

/** Unique people per task in the scope; null for a task this reader cannot judge. Never summed. */
export function countTasks(scoped: Row[], accessKnown: boolean): Record<TaskKey, number | null> {
  const out = {} as Record<TaskKey, number | null>;
  for (const k of TASKS) {
    let n = 0;
    let known = true;
    for (const u of scoped) {
      const v = inTask(u, k, accessKnown);
      if (v == null) known = false;
      else if (v) n++;
    }
    out[k] = known ? n : null;
  }
  return out;
}

/** The address, read: ?q=, ?team=, ?vis=, ?oppgave=. Older links keep landing on their list. */
export function readScope(sp: URLSearchParams): Scope & { task: Task } {
  const t = sp.get('team');
  const team = t === 'none' ? 'none' : t && /^\d+$/.test(t) ? Number(t) : null;
  const o = sp.get('oppgave');
  let task: Task =
    o === 'ansattnummer'
      ? 'number'
      : ((Object.keys(PARAM) as TaskKey[]).find((k) => PARAM[k] === o) ?? 'all');
  // Hjem, Lønn and Oversikt link ?mangler=1 or ?oppsett=mangler (employee number and setup), Lønn's
  // Kontroller ?oppsett=uke (no agreed week), the welcome ?app=ikke (people not yet in)
  if (!o) {
    if (sp.get('oppsett') === 'uke') task = 'hours';
    else if (sp.get('oppsett') === 'mangler' || sp.get('mangler') === '1') task = 'setup';
    else if (sp.get('app') === 'ikke') task = 'uninvited';
  }
  return { q: (sp.get('q') ?? '').slice(0, 100), team, who: whoOf(sp.get('vis')), task };
}
/** The address, written: only what differs from the default, so /ansatte stays /ansatte. */
export function writeScope(s: Scope & { task: Task }): URLSearchParams {
  const n = new URLSearchParams();
  if (s.q.trim()) n.set('q', s.q);
  if (s.team != null) n.set('team', String(s.team));
  if (s.who !== 'aktive') n.set('vis', s.who);
  if (s.task === 'number') n.set('oppgave', 'ansattnummer');
  else if (s.task !== 'all') n.set('oppgave', PARAM[s.task]);
  return n;
}

/**
 * The correction queue (#270 EMP-02, EMP-07): the list a manager opened a person from, kept for the
 * tab by stable ids, so «Lagre og neste» and the profile's arrows walk that list, and «Tilbake» lands
 * on it with the same task, team, status and search. Session storage, scoped to company and user, so
 * another company or another person on this browser never inherits it.
 */
export interface Queue {
  tenant: number;
  user: number;
  /** the list's address, /ansatte?… */
  origin: string;
  /** the people the list showed, in its order, when the person was opened */
  ids: number[];
  task: Task;
  /** where Tilbake puts the keyboard and the page */
  focus: number;
  scroll: number;
}
const QKEY = 'timefly.ansatte.ko';
export function saveQueue(q: Queue) {
  try {
    sessionStorage.setItem(QKEY, JSON.stringify(q));
  } catch {
    /* private mode: the profile falls back to the whole list */
  }
}
export function loadQueue(tenant: number | undefined, user: number | undefined): Queue | null {
  try {
    const q = JSON.parse(sessionStorage.getItem(QKEY) ?? 'null') as Queue | null;
    return q && q.tenant === tenant && q.user === user ? q : null;
  } catch {
    return null;
  }
}

/**
 * The next person in the queue after `id` who still needs the task, from today's facts (a save may
 * have resolved others meanwhile); wraps round once. By id, never by position, so a row that left the
 * list cannot shift who comes next.
 */
export function nextInQueue(q: Queue, id: number, users: Row[], accessKnown: boolean): Row | null {
  const byId = new Map(users.map((u) => [u.id, u]));
  const i = q.ids.indexOf(id);
  const order = i < 0 ? q.ids : [...q.ids.slice(i + 1), ...q.ids.slice(0, i)];
  for (const n of order) {
    const u = byId.get(n);
    if (u && u.id !== id && inTask(u, q.task, accessKnown)) return u;
  }
  return null;
}
/** How many in the queue still need its task. */
export const remaining = (q: Queue, users: Row[], accessKnown: boolean) =>
  users.filter((u) => q.ids.includes(u.id) && inTask(u, q.task, accessKnown)).length;

/** The editor field a task's correction opens on. */
export function fieldFor(u: Row, task: Task): string | null {
  if (task === 'hours') return 'agreed_week_hours';
  if (task === 'setup') {
    const g = setupGaps(u)?.[0] ?? null;
    return g === 'number_format' ? (u.number_problem?.field ?? 'employee_id') : g;
  }
  if (task === 'number') return 'employee_id';
  return null;
}
